import { useEffect, useState } from 'react';
import { CircularProgress } from '@rmwc/circular-progress';
import { TransactionApi } from '@recargas-dominicanas/core/api';
import { formatCurrency, useAsyncEffect, useForm, validators } from '@recargas-dominicanas/core/utils';
import type { InsurancePlan, InsuranceVehicle, IProduct, ITransaction } from '@recargas-dominicanas/core/types';
import { LoadingModal, Select, SelectOption, TextField } from '@recargas-dominicanas/core/components';
import { useBusiness, useSales, useSalesReport, useTransactions } from '@recargas-dominicanas/core/store';
import { InsuranceConfirmModal } from '../insurance-confirm-modal/insurance-confirm-modal.component';
import { InsuranceModal } from '../insurance-modal/insurance-modal.component';
import { ErrorModal } from '../error-modal/error-modal.component';
import { printInsurance } from '../../utils/printInsurance';
import { style } from './insurance.module.css';

import { 
  ProductCard, 
  ProductCardButtons, 
  ProductCardContent, 
  ProductCardFields, 
  ProductCardInfo, 
  ProductCardTitle 
} from '../product-card';

interface Props {
  product: IProduct;
}

export function Insurance(props: Props) {

  const { product } = props;

  const businessStore = useBusiness();
  const salesReports = useSalesReport();
  const transactions = useTransactions();
  const salesStore = useSales();
  const salesReport = salesReports.getCurrent();
  const business = businessStore.get();
  const [enabledTransition, setEnabledTransition] = useState(false);
  const [vehicles, setVechicles] = useState<InsuranceVehicle[]>([]);
  const [plans, setPlans] = useState<InsurancePlan[]>([]);
  const [transaction, setTransaction] = useState<ITransaction>();
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [errorVehicles, setErrorVehicles] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [errorPlans, setErrorPlans] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openLoadingModal, setOpenLoadingModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openInsufficientFundsModal, setOpenInsufficientFundsModal] = useState(false);
  const [openSuccessfulModal, setOpenSuccessfulModal] = useState(false);
  
  const form = useForm({ 
    vehicle: undefined as InsuranceVehicle | undefined,
    plan: undefined as InsurancePlan | undefined,
    chassis: '',
    docNumber: '',
    phone: ''
  });

  useAsyncEffect(async () => {
    try {
      setLoadingVehicles(true);
      setErrorVehicles(false);
      const vehicles = await TransactionApi.getInsuranceVehicles(product.id);
      setVechicles(vehicles);
      setLoadingVehicles(false);
    } catch {
      setLoadingVehicles(false);
      setErrorVehicles(true);
    }
  }, [product.id]);

  useEffect(() => {
    form.clear();
    setEnabledTransition(true);
    setTimeout(() => setEnabledTransition(false), 400);
  }, [product.id]);

  async function handleVehicleChange(vehicle?: InsuranceVehicle) {

    form.fields.plan.clear();
    
    try {
      setLoadingPlans(true);
      setErrorPlans(false);
      const plans = await TransactionApi.getInsurancePlans(product.id, vehicle!.type);
      setPlans(plans);
      setLoadingPlans(false);
    } catch {
      setLoadingPlans(false);
      setErrorPlans(true);
    }
  }

  function handleOpenConfirmModal() {
    if (form.isValid()) {
      setOpenConfirmModal(true);
    }
  }

  async function handleBuyInsurance() {

    if (!form.isValid()) return;

    try {

      setOpenConfirmModal(false);

      if (salesReport.balance < form.fields.plan.value!.price) {
        setOpenInsufficientFundsModal(true);
        return;
      }

      setOpenLoadingModal(true);

      const transaction = await TransactionApi.buyInsurance(product.id, {
        vehicleType: form.fields.vehicle.value!.type,
        vigency: form.fields.plan.value!.vigency,
        chassis: form.fields.chassis.value,
        docNumber: form.fields.docNumber.value,
        phone: form.fields.phone.value
      });

      salesStore.fetch();
      salesReports.fetchCurrent();
      transactions.fetchGroupByDay(1, 50);
      form.clear();
      
      setOpenLoadingModal(false);
      setOpenSuccessfulModal(true);
      setTransaction(transaction);

    } catch {
      
      setOpenLoadingModal(false);
      setOpenErrorModal(true);
    }
  }

  return (
    <ProductCard>
      <ProductCardTitle title='Seguro'/>
      <ProductCardContent>
        <ProductCardInfo 
          product={product}
          enabledTransition={enabledTransition}/>
        <ProductCardFields enabledTransition={enabledTransition}>
          <Select 
            style={style.select} 
            label='Vehiculo'
            required={true}
            formField={form.fields.vehicle}
            onChange={handleVehicleChange}>
            {loadingVehicles &&
              // @ts-ignore
              <CircularProgress
                className={style.circularProgress} 
                size={44}/>
            }
            {errorVehicles &&
              <div className={style.selectError}>Error cargando datos</div>
            }
            {!loadingVehicles && !errorVehicles && vehicles.map(vehicle =>
              <SelectOption
                key={vehicle.type}
                value={vehicle}
                showClearIcon={false}
                label={vehicle.name}>
                {vehicle.name}
              </SelectOption>
            )}
          </Select>
          <Select 
            style={style.select} 
            label='Plan'
            required={true}
            formField={form.fields.plan}
            disabled={form.fields.vehicle.value === undefined}>
            {loadingPlans &&
              // @ts-ignore
              <CircularProgress
                className={style.circularProgress} 
                size={44}/>
            }
            {errorPlans &&
              <div className={style.selectError}>Error cargando datos</div>
            }
            {!loadingPlans && !errorPlans && plans.map(plan =>
              <SelectOption
                key={plan.name}
                style={style.selectOption}
                value={plan}
                showClearIcon={false}
                label={plan.name}>
                <div className={style.planItem}>
                  <div>{plan.name}</div>
                  <div className={style.planPrice}>{formatCurrency(plan.price)}</div>
                </div>
              </SelectOption>
            )}
          </Select>
          <TextField
            formField={form.fields.chassis}
            label='Chasis'
            validators={[validators.required]}/>
          <TextField
            formField={form.fields.docNumber}
            label='Cédula'
            validators={[validators.required]}/>
          <TextField
            formField={form.fields.phone}
            label='Télefono'
            validators={[validators.required]}/>
        </ProductCardFields>
      </ProductCardContent>
      <ProductCardButtons
        onAccept={handleOpenConfirmModal}
        onCancel={() => form.clear()}/>
      {form.value.vehicle && form.value.plan &&
        <InsuranceConfirmModal
          open={openConfirmModal}
          product={product}
          vehicle={form.value.vehicle}
          plan={form.value.plan}
          chassis={form.value.chassis}
          docNumber={form.value.docNumber}
          phone={form.value.phone}
          onClose={() => setOpenConfirmModal(false)}
          onAccept={handleBuyInsurance}/>
      }
      <LoadingModal
        open={openLoadingModal}
        title='Realizando compra'/>
      {transaction &&
        <InsuranceModal
          open={openSuccessfulModal}
          title='Compra realizada'
          transaction={transaction}
          onPrint={() => printInsurance(business, transaction)}
          onClose={() => setOpenSuccessfulModal(false)}/>
      }
      <ErrorModal
        open={openInsufficientFundsModal}
        title='Balance insuficiente'
        description='No posee balance suficiente para realizar esta operación.'
        onClose={() => setOpenInsufficientFundsModal(false)}/>
      <ErrorModal
        open={openErrorModal}
        title='No se pudo realizar la compra'
        description='Ocurrió un error al realizar la compra. Inténtelo de nuevo más tarde.'
        onClose={() => setOpenErrorModal(false)}/>
    </ProductCard>
  );
}
